/**
 * The session, as the rest of the app sees it.
 *
 * Kept separate from the provider component so that importing `useAuth` never
 * drags a component into a module and breaks fast refresh.
 */
import { createContext, useContext } from 'react';

import type { LoginRequest, SessionUser } from '@wend/shared';

/**
 * `loading` is the honest starting state: on a cold load the app does not yet
 * know whether the httpOnly refresh cookie will produce a session, and guessing
 * would flash the login screen at an authenticated user.
 */
export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthContextValue {
  status: AuthStatus;
  /** The signed-in user, or `null` unless `status === 'authenticated'`. */
  user: SessionUser | null;
  /** Signs in and adopts the session. Throws `ApiError` on failure. */
  signIn: (credentials: LoginRequest) => Promise<SessionUser>;
  /** Revokes the refresh token and clears local state. Never throws. */
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/** Throws outside `<AuthProvider>` so the mistake is loud, not silent. */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth() must be used inside an <AuthProvider>.');
  }
  return context;
}
